import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useSnackbar } from "notistack";
import type { Metier } from "models";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import metiersService from "services/metiersService";
import ToastMessages from "constants/ToastMessages";

type CreationMetierProps = {
  creation: boolean;
  modification?: never;
  metier?: never;
};
type ModificationMetierProps = {
  creation?: never;
  modification: boolean;
  metier: Metier;
};

type MetierFormProps = {
  open: boolean;
  onClose: () => void;
  onMetierChanged: (metier: Metier) => void;
} & (CreationMetierProps | ModificationMetierProps);

function MetierDialog({
  open,
  onClose,
  onMetierChanged,
  creation,
  modification,
  metier,
}: Readonly<MetierFormProps>): ReactNode {
  const { enqueueSnackbar } = useSnackbar();
  const [libelle, setLibelle] = useState<string>(metier?.libelle ?? "");
  useEffect(() => {
    setLibelle(metier?.libelle ?? "");
  }, [metier]);
  const onConfirm = useCallback(async () => {
    if (libelle == null || libelle === "") {
      return;
    }

    try {
      if (modification === true) {
        const metierUpdated = await metiersService.patch(metier.id, [
          { op: "replace", path: "libelle", value: libelle },
        ]);
        onMetierChanged(metierUpdated);
      } else {
        const metierCreated = await metiersService.create({ libelle });
        onMetierChanged(metierCreated);
      }
    } catch (error: unknown) {
      const message: string = (error as Error)?.message ?? ToastMessages.ERROR;
      enqueueSnackbar(message, { variant: "error" });
      return;
    }
    onClose();
  }, [libelle, onClose, modification, metier, onMetierChanged, enqueueSnackbar]);

  const title = creation === true ? "Création" : "Modification";

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={() => {
        onClose();
      }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ overflowY: "visible" }}>
        <TextField
          size="small"
          fullWidth
          label="Libellé"
          variant="outlined"
          value={libelle}
          onChange={(e) => {
            setLibelle(e.target.value ?? "");
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Annuler
        </Button>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <Button variant="contained" color="primary" onClick={onConfirm}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MetierDialog;
